import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "../lib/supabase";
import {
  normalizeAadhaar,
  isValidAadhaar,
  isValidGST,
} from "../utils/kycUtils";

const SKILL_OPTIONS = [
  "Plumbing",
  "Electrical",
  "Carpentry",
  "Painting",
  "Masonry",
  "Tile Work",
  "Welding",
  "False Ceiling",
  "Waterproofing",
  "Fabrication",
  "CCTV",
  "Solar",
  "AC Repair",
  "Helper",
];

function Signup() {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    fullName: "",
    phone: "",
    email: "",
    password: "",
    dob: "",
    role: "customer",
    city: "Hyderabad",
    businessName: "",
    aadhaarNumber: "",
    gstNumber: "",
    shopName: "",
    flatNo: "",
    streetArea: "",
    state: "Telangana",
    country: "India",
    pincode: "",
    selectedSkills: [],
  });

  const [loading, setLoading] = useState(false);

  const generateDisplayId = (role) => {
    const prefix =
      role === "customer"
        ? "MB-CUS"
        : role === "worker"
        ? "MB-WRK"
        : role === "shop_owner"
        ? "MB-SHP"
        : "MB-USR";

    return `${prefix}-${Date.now()}`;
  };

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const toggleSkill = (skill) => {
    setFormData((prev) => {
      const exists = prev.selectedSkills.includes(skill);

      return {
        ...prev,
        selectedSkills: exists
          ? prev.selectedSkills.filter((item) => item !== skill)
          : [...prev.selectedSkills, skill],
      };
    });
  };

  const isAdult = (dob) => {
    if (!dob) return false;
    const age = new Date().getFullYear() - new Date(dob).getFullYear();
    return age >= 18;
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (!formData.phone.trim()) {
        alert("Phone number is mandatory.");
        return;
      }

      if (!formData.password.trim() || formData.password.length < 6) {
        throw new Error("Password must be at least 6 characters.");
      }

      if (formData.role === "worker") {
        if (!formData.aadhaarNumber.trim()) {
          alert("Aadhaar number is mandatory for workers.");
          return;
        }

        const normalizedAadhaar = normalizeAadhaar(formData.aadhaarNumber);

        if (!isValidAadhaar(normalizedAadhaar)) {
          alert("Please enter a valid 12-digit Aadhaar number.");
          return;
        }

        if (!formData.dob || !isAdult(formData.dob)) {
          alert("Workers must be 18 years or above.");
          return;
        }
      }

      if (formData.role === "shop_owner") {
        if (!formData.aadhaarNumber.trim()) {
          alert("Aadhaar number is mandatory for shop owners.");
          return;
        }

        const normalizedAadhaar = normalizeAadhaar(formData.aadhaarNumber);

        if (!isValidAadhaar(normalizedAadhaar)) {
          alert("Please enter a valid 12-digit Aadhaar number.");
          return;
        }

        if (!formData.gstNumber.trim()) {
          alert("GST number is mandatory for shop owners.");
          return;
        }

        if (!isValidGST(formData.gstNumber)) {
          alert("Please enter a valid GST number.");
          return;
        }

        if (!formData.shopName.trim()) {
          alert("Shop name is mandatory.");
          return;
        }

        if (!formData.dob || !isAdult(formData.dob)) {
          alert("Shop owners must be 18 years or above.");
          return;
        }
      }

      if (!formData.fullName.trim()) {
        alert("Full name is mandatory.");
        return;
      }

      const phone = formData.phone.trim();
      const normalizedAadhaar = normalizeAadhaar(formData.aadhaarNumber);
      const normalizedGst = formData.gstNumber.trim().toUpperCase();
      const trimmedBusinessName = formData.businessName.trim();
      const trimmedShopName = formData.shopName.trim();

      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        phone,
        password: formData.password,
      });

      if (signUpError) throw signUpError;

      const authUser = signUpData.user;
      if (!authUser) {
        throw new Error("Signup failed. User not created.");
      }

      const displayUserId = generateDisplayId(formData.role);

      sessionStorage.setItem(
        "mistribazaarPendingSignup",
        JSON.stringify({
          userId: authUser.id,
          fullName: formData.fullName,
          phone,
          email: formData.email.trim(),
          role: formData.role,
          city: formData.city,
          businessName: trimmedBusinessName,
          aadhaarNumber: normalizedAadhaar,
          gstNumber: normalizedGst,
          shopName: trimmedShopName,
          flatNo: formData.flatNo.trim(),
          streetArea: formData.streetArea.trim(),
          state: formData.state.trim(),
          country: formData.country.trim(),
          pincode: formData.pincode.trim(),
          selectedSkills: formData.selectedSkills,
          displayUserId,
        })
      );
      localStorage.removeItem("mistribazaarPendingSignup");

      const { error: otpError } = await supabase.auth.signInWithOtp({
        phone,
        options: {
          shouldCreateUser: false,
        },
      });

      if (otpError) throw otpError;

      alert("OTP sent to your phone.");
      navigate("/verify-otp");
    } catch (error) {
      console.error("Signup error:", error);
      alert(error.message || "Signup failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={page}>
      <div style={wrapper} className="auth-page__wrapper">
        <div style={hero} className="auth-page__hero">
          <div style={heroBadge}>Get started</div>
          <h1 style={heroTitle} className="auth-page__title">Create your MistriBazaar account</h1>
          <p style={heroText} className="auth-page__hero-text">
            Phone is mandatory. Email is optional and can be linked later for recovery and email login.
          </p>
        </div>

        <div style={card} className="auth-page__card">
          <h2 style={{ marginTop: 0 }}>Sign Up</h2>

          <form onSubmit={handleSignup}>
            <label style={label}>Full Name</label>
            <input
              name="fullName"
              placeholder="Enter full name"
              value={formData.fullName}
              onChange={handleChange}
              required
              style={input}
            />

            <label style={label}>Phone Number</label>
            <input
              name="phone"
              placeholder="+91XXXXXXXXXX"
              value={formData.phone}
              onChange={handleChange}
              required
              style={input}
            />

            <label style={label}>Email (optional)</label>
            <input
              name="email"
              type="email"
              placeholder="Enter email"
              value={formData.email}
              onChange={handleChange}
              style={input}
            />

            <label style={label}>Password</label>
            <input
              name="password"
              type="password"
              placeholder="Create password"
              value={formData.password}
              onChange={handleChange}
              required
              style={input}
            />

            <label style={label}>Date of Birth</label>
            <input
              name="dob"
              type="date"
              required={formData.role === "worker" || formData.role === "shop_owner"}
              value={formData.dob}
              onChange={handleChange}
              style={input}
            />

            <label style={label}>Role</label>
            <select
              name="role"
              value={formData.role}
              onChange={handleChange}
              style={input}
            >
              <option value="customer">Customer</option>
              <option value="worker">Worker</option>
              <option value="shop_owner">Shop Owner</option>
            </select>

            <label style={label}>City</label>
            <input
              name="city"
              placeholder="Enter city"
              value={formData.city}
              onChange={handleChange}
              style={input}
            />

            {formData.role === "worker" || formData.role === "shop_owner" ? (
              <>
                <label style={label}>Aadhaar Number</label>
                <input
                  name="aadhaarNumber"
                  placeholder="Enter 12-digit Aadhaar number"
                  value={formData.aadhaarNumber}
                  onChange={handleChange}
                  style={input}
                />
              </>
            ) : null}

            {formData.role === "worker" ? (
              <>
                <label style={label}>Select Skills</label>
                <div style={chipWrap}>
                  {SKILL_OPTIONS.map((skill) => {
                    const selected = formData.selectedSkills.includes(skill);

                    return (
                      <button
                        key={skill}
                        type="button"
                        onClick={() => toggleSkill(skill)}
                        style={{
                          ...chipBtn,
                          background: selected ? "#fff" : "#111",
                          color: selected ? "#000" : "#fff",
                        }}
                      >
                        {skill}
                      </button>
                    );
                  })}
                </div>
              </>
            ) : null}

            {formData.role === "shop_owner" ? (
              <>
                <label style={label}>Business / Shop Name</label>
                <input
                  name="businessName"
                  placeholder="Business / Shop Name"
                  value={formData.businessName}
                  onChange={handleChange}
                  style={input}
                />

                <label style={label}>Shop Name</label>
                <input
                  name="shopName"
                  placeholder="Public shop name"
                  value={formData.shopName}
                  onChange={handleChange}
                  style={input}
                />

                <label style={label}>GST Number</label>
                <input
                  name="gstNumber"
                  placeholder="Enter GST number"
                  value={formData.gstNumber}
                  onChange={handleChange}
                  style={input}
                />

                <label style={label}>Flat No / House No</label>
                <input
                  name="flatNo"
                  placeholder="Flat No / House No"
                  value={formData.flatNo}
                  onChange={handleChange}
                  style={input}
                />

                <label style={label}>Street / Area</label>
                <input
                  name="streetArea"
                  placeholder="Street / Area"
                  value={formData.streetArea}
                  onChange={handleChange}
                  style={input}
                />

                <label style={label}>State</label>
                <input
                  name="state"
                  placeholder="State"
                  value={formData.state}
                  onChange={handleChange}
                  style={input}
                />

                <label style={label}>Country</label>
                <input
                  name="country"
                  placeholder="Country"
                  value={formData.country}
                  onChange={handleChange}
                  style={input}
                />

                <label style={label}>Pincode</label>
                <input
                  name="pincode"
                  placeholder="Pincode"
                  value={formData.pincode}
                  onChange={handleChange}
                  style={input}
                />
              </>
            ) : null}

            <button type="submit" disabled={loading} style={button}>
              {loading ? "Creating account..." : "Create Account"}
            </button>
          </form>

          <p style={footerText}>
            Already have an account?{" "}
            <Link to="/login" style={link}>
              Login
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

const page = {
  background: "radial-gradient(circle at top, #101010 0%, #000 55%)",
  minHeight: "100vh",
  padding: "40px 20px",
  color: "#fff",
  fontFamily: "Arial, sans-serif",
};

const wrapper = {
  maxWidth: "1100px",
  margin: "0 auto",
  display: "grid",
  gridTemplateColumns: "1.1fr 0.9fr",
  gap: "24px",
  alignItems: "center",
};

const hero = {
  background: "linear-gradient(135deg, #111, #191919)",
  border: "1px solid #222",
  borderRadius: "22px",
  padding: "30px",
};

const heroBadge = {
  display: "inline-block",
  padding: "8px 14px",
  borderRadius: "999px",
  border: "1px solid #2a2a2a",
  background: "#0d0d0d",
  color: "#d1d1d1",
  fontSize: "13px",
  marginBottom: "18px",
};

const heroTitle = { margin: 0, fontSize: "40px" };
const heroText = { marginTop: "14px", color: "#d1d1d1", lineHeight: "1.8" };

const card = {
  background: "#111",
  padding: "26px",
  borderRadius: "20px",
  border: "1px solid #2a2a2a",
  boxShadow: "0 12px 32px rgba(0,0,0,0.22)",
};

const label = {
  display: "block",
  marginBottom: "8px",
  marginTop: "12px",
  color: "#fff",
};

const input = {
  width: "100%",
  padding: "12px 14px",
  marginBottom: "12px",
  borderRadius: "10px",
  border: "1px solid #2a2a2a",
  background: "#0b0b0b",
  color: "#fff",
  boxSizing: "border-box",
};

const button = {
  width: "100%",
  padding: "12px 16px",
  borderRadius: "10px",
  border: "none",
  background: "#fff",
  color: "#000",
  fontWeight: "bold",
  cursor: "pointer",
  marginTop: "10px",
};

const chipWrap = {
  display: "flex",
  gap: "10px",
  flexWrap: "wrap",
  marginBottom: "12px",
};

const chipBtn = {
  padding: "10px 14px",
  border: "1px solid #2a2a2a",
  borderRadius: "999px",
  cursor: "pointer",
};

const footerText = { color: "#c9c9c9", marginTop: "18px", marginBottom: 0 };
const link = { color: "#fff" };

export default Signup;
